import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, CircleDollarSign, Copy, FileWarning, Link2,
  Receipt, ShieldCheck, Wallet,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

/**
 * How reimbursement works — in the app, not in a document nobody opens.
 *
 * Written for two audiences at once: a committee member deciding whether the
 * controls are sound, and a volunteer wondering when they'll get their money.
 * Both need plain language, so there is no jargon here and no state diagram.
 */

const STEPS = [
  {
    who: "Volunteer",
    tone: "text-blue-600",
    title: "Submit",
    body: "Opens a link — no account needed. Amount, what it was for, and a photo of the "
      + "receipt taken on the spot. They get a reference by email and can check on it "
      + "any time.",
  },
  {
    who: "Event host or team lead",
    tone: "text-violet-600",
    title: "Approve",
    body: "Checks the receipt matches what was claimed. They can approve it, reduce it to "
      + "match the receipt, ask a question, or decline. They cannot approve more than was "
      + "asked for.",
  },
  {
    who: "Admin or sub-admin",
    tone: "text-emerald-600",
    title: "Pay",
    body: "Pays from the bank, then records it here against a reference. Claims are grouped "
      + "by person, so one transfer covers everything that person is owed.",
  },
];

const RULES = [
  {
    icon: ShieldCheck,
    title: "Approving and paying are separate",
    body: "\u201cIs this a real expense?\u201d is a question for whoever ran the event. "
      + "\u201cHas the money left the account?\u201d is a question for whoever holds it. "
      + "Different people, enforced on the server \u2014 not just hidden in the interface.",
  },
  {
    icon: Receipt,
    title: "Receipts required, with an honest exception",
    body: "People lose receipts. A claim can go in without one if a reason is given, and "
      + "that reason is shown to the approver rather than quietly treated the same as "
      + "everything else.",
  },
  {
    icon: CircleDollarSign,
    title: "The app never moves money",
    body: "It records that somebody did, with a reference. No bank details are stored "
      + "anywhere \u2014 that would turn a volunteer tool into something with real breach "
      + "consequences.",
  },
  {
    icon: FileWarning,
    title: "Nothing is silently rounded",
    body: "Amounts are held to the exact cent. An amount that cannot be read precisely is "
      + "refused rather than rounded, because the person short-changed is a volunteer who "
      + "paid out of their own pocket.",
  },
];

export default function ClaimsProcessPage() {
  const navigate = useNavigate();
  const { isAdmin, isSubAdmin } = useAuth();
  const [copied, setCopied] = useState(false);

  // The claim link is per event, so this page explains where to find it rather
  // than pretending there is a single URL.
  const copyHint = async () => {
    const text = `${window.location.origin}/claim/<event share link code>`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Copied the link pattern");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="p-4 md:p-8 w-full space-y-6 pb-24">
      <Button variant="ghost" size="sm" className="-ml-2 gap-1.5"
              onClick={() => navigate("/claims")} data-testid="process-back">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Overview
      </Button>

      <header>
        <h1 className="text-2xl font-bold text-foreground">How reimbursement works</h1>
        <p className="text-sm text-muted-foreground mt-1">
          What happens between a volunteer spending their own money and getting it back.
        </p>
      </header>

      <section aria-labelledby="steps-heading" className="space-y-3">
        <h2 id="steps-heading" className="font-semibold text-foreground">
          Three steps, three different people
        </h2>
        {STEPS.map((step, i) => (
          <Card key={step.title} data-testid={`step-${i + 1}`}>
            <CardContent className="p-5 flex gap-4">
              <div className="shrink-0">
                <span className="h-9 w-9 rounded-full bg-primary/10 text-primary
                                 flex items-center justify-center font-bold text-sm">
                  {i + 1}
                </span>
              </div>
              <div className="min-w-0">
                <p className={`text-xs font-semibold uppercase tracking-wide ${step.tone}`}>
                  {step.who}
                </p>
                <p className="font-semibold text-foreground mt-0.5">{step.title}</p>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  {step.body}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>

      <section aria-labelledby="link-heading">
        <Card>
          <CardContent className="p-5 space-y-3">
            <h2 id="link-heading" className="font-semibold text-foreground flex items-center gap-1.5">
              <Link2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              Where volunteers claim
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Each event has its own claim link, built from that event's share code. Open the
              event, generate or copy its share link, and the claim form lives at the same
              code under <span className="font-mono text-xs">/claim/</span>.
            </p>
            <Button variant="outline" size="sm" className="h-10 gap-1.5" onClick={copyHint}
                    data-testid="process-copy-pattern">
              {copied ? <Check className="h-4 w-4" aria-hidden="true" />
                      : <Copy className="h-4 w-4" aria-hidden="true" />}
              Copy the link pattern
            </Button>
          </CardContent>
        </Card>
      </section>

      <section aria-labelledby="rules-heading" className="space-y-3">
        <h2 id="rules-heading" className="font-semibold text-foreground">
          Controls built in
        </h2>
        {RULES.map((rule) => (
          <Card key={rule.title}>
            <CardContent className="p-5 flex gap-3">
              <rule.icon className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="font-medium text-foreground">{rule.title}</p>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  {rule.body}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>

      <section aria-labelledby="who-heading">
        <Card>
          <CardContent className="p-5 space-y-3">
            <h2 id="who-heading" className="font-semibold text-foreground">Who can do what</h2>
            <div className="space-y-2 text-sm">
              {[
                ["Volunteer with a link", ["Submit", "Track their own"]],
                ["Event host", ["Submit", "Approve"]],
                ["Team lead", ["Submit", "Approve"]],
                ["Sub-admin", ["Submit", "Approve", "Pay"]],
                ["Admin", ["Submit", "Approve", "Pay"]],
              ].map(([role, can]) => (
                <div key={role} className="flex flex-wrap items-center justify-between gap-2
                                           border-b border-border pb-2 last:border-0">
                  <span className="text-foreground">{role}</span>
                  <span className="flex flex-wrap gap-1.5">
                    {can.map((c) => (
                      <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
                    ))}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Vendors have no access to reimbursements at all.
            </p>
          </CardContent>
        </Card>
      </section>

      <div className="flex flex-wrap gap-2">
        <Button className="h-11 gap-1.5" onClick={() => navigate("/claims/review")}
                data-testid="process-goto-review">
          Review claims
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Button>
        {(isAdmin || isSubAdmin) && (
          <Button variant="outline" className="h-11 gap-1.5"
                  onClick={() => navigate("/claims/payments")}
                  data-testid="process-goto-payments">
            <Wallet className="h-4 w-4" aria-hidden="true" />
            Payment run
          </Button>
        )}
      </div>
    </div>
  );
}
